import { fork, put, all, call, takeLatest } from "redux-saga/effects";

import {
  getOrganizationalUnitsSuccess,
  getOrganizationalUnitsFailure,
  generateYaml,
  generateYamlSuccess,
  generateYamlFailure,
  generateEventInsightYaml,
  generateEventInsightYamlSuccess,
  generateEventInsightYamlFailure,
  checkStackStatusMethod,
  stackStatusResponseMethod,
  stackStatusErrorMethod,
  checkAccountActivityMethod,
  accountActivitySuccessMethod,
  accountActivityErrorMethod,
} from "./multiAccountSlice";

import {
  getOrganizationalUnits as getOrganizationalUnitsApi,
  generateYaml as generateYamlApi,
  generateEventInsightYaml as generateEventInsightYamlApi,
  checkAccountStatus as checkAccountStatusApi,
  checkStackStatus as checkStackStatusApi,
} from "../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";

// 🔁 Worker: Fetch Org Units
function* fetchOrganizationalUnits({ payload: awsAccountId }) {
  try {
    console.log(
      "Saga: Calling getOrganizationalUnits with awsAccountId =",
      awsAccountId
    );
    const response = yield call(getOrganizationalUnitsApi, awsAccountId);
    console.log("Saga: Response =", response);

    if (response.status === 200) {
      delete response.status;
      const units = response.organizationalUnits;
      const hasUnits = Array.isArray(units) && units.length > 0;
      const apiMessage = getApiErrorMessage(response);
      const hasServerMessage =
        apiMessage !== "Request failed." &&
        apiMessage !==
          "An unexpected error occurred. Please try again later.";

      // Some APIs return HTTP 200 with an error message and empty org data
      if (!hasUnits && hasServerMessage) {
        yield put(getOrganizationalUnitsFailure(apiMessage));
      } else if (!hasUnits) {
        yield put(
          getOrganizationalUnitsFailure(
            "We couldn't load organization information for this account. Verify the cross-account trust role and IAM permissions in the master account, then try again."
          )
        );
      } else {
        yield put(getOrganizationalUnitsSuccess(response));
      }
    } else {
      yield put(
        getOrganizationalUnitsFailure(
          getApiErrorMessage(response)
        )
      );
    }
  } catch (error) {
    if (error.response) {
      yield put(
        getOrganizationalUnitsFailure(getApiErrorMessage(error.response))
      );
    } else if (error.request) {
      yield put(
        getOrganizationalUnitsFailure("No response received from server.")
      );
    } else {
      yield put(
        getOrganizationalUnitsFailure(`Request error: ${error.message}`)
      );
    }
  }
}

// 📄 Worker: Generate YAML
function* generateYamlSaga({ payload }) {
  try {
    console.log("Saga: Calling generateYaml with payload =", payload);
    const response = yield call(generateYamlApi, payload);
    console.log("Saga: YAML Response =", response);

    if (response.status === 200) {
      yield put(generateYamlSuccess(response.data));
    } else {
      yield put(
        generateYamlFailure(
          response.data
            ? response.data
            : `Unexpected response: ${response.status}`
        )
      );
    }
  } catch (error) {
    if (error.response) {
      yield put(
        generateYamlFailure(
          `Error ${error.response.status}: ${error.response.data}`
        )
      );
    } else if (error.request) {
      yield put(generateYamlFailure("No response received from server."));
    } else {
      yield put(generateYamlFailure(`Request error: ${error.message}`));
    }
  }
}

// 📄 Worker: Generate Event Insight YAML
function* generateEventInsightYamlSaga({ payload }) {
  try {
    console.log("Saga: Calling generateEventInsightYaml with payload =", payload);
    const response = yield call(generateEventInsightYamlApi, payload);
    console.log("Saga: Event Insight YAML Response =", response);

    if (response.status === 200) {
      yield put(generateEventInsightYamlSuccess(response.data));
    } else {
      yield put(
        generateEventInsightYamlFailure(
          response.data
            ? response.data
            : `Unexpected response: ${response.status}`
        )
      );
    }
  } catch (error) {
    if (error.response) {
      yield put(
        generateEventInsightYamlFailure(
          `Error ${error.response.status}: ${error.response.data}`
        )
      );
    } else if (error.request) {
      yield put(generateEventInsightYamlFailure("No response received from server."));
    } else {
      yield put(generateEventInsightYamlFailure(`Request error: ${error.message}`));
    }
  }
}

// 🔁 Worker: Fetch stack status payload orgCode, masterAccountNumber, stackName, externalId, roleSessionName, memberAccountIds
function* fetchStackStatusSaga({ payload }) {
  try {
    console.log("Saga: Calling fetchStackStatusSaga with payload =", payload);
    const response = yield call(checkStackStatusApi, payload);
    console.log("Saga: Raw Response =", response);

    if (response.status === 200) {
      // Remove status from response
      const { status, ...rest } = response;

      // If rest looks like a spread string (all numeric keys), rebuild the string
      const isStringObject =
        Object.keys(rest).every((key) => !isNaN(key)) &&
        Object.values(rest).every((val) => typeof val === "string");

      const finalResult = isStringObject ? Object.values(rest).join("") : rest;

      yield put(stackStatusResponseMethod(finalResult));
    } else {
      yield put(stackStatusErrorMethod(response.data));
    }
  } catch (error) {
    if (error.response) {
      yield put(
        stackStatusErrorMethod(
          `Error ${error.response.status}: ${error.response.data}`
        )
      );
    } else if (error.request) {
      yield put(stackStatusErrorMethod("No response received from server."));
    } else {
      yield put(stackStatusErrorMethod(`Request error: ${error.message}`));
    }
  }
}

// 🔁 Worker: Fetch account activity status
function* fetchAccountStatusSaga({ payload }) {
  try {
    console.log("Saga: Calling fetchAccountStatusSaga with payload =", payload);
    const response = yield call(checkAccountStatusApi, payload);
    console.log("Saga: Account Status Raw Response =", response);

    if (response.status === 200) {
      // Remove status from response
      const { status, ...rest } = response;

      // If rest looks like a spread string (all numeric keys), rebuild the string
      const isStringObject =
        Object.keys(rest).every((key) => !isNaN(key)) &&
        Object.values(rest).every((val) => typeof val === "string");

      const finalResult = isStringObject ? Object.values(rest).join("") : rest;

      yield put(accountActivitySuccessMethod(finalResult));
    } else {
      yield put(accountActivityErrorMethod(response.data));
    }
  } catch (error) {
    if (error.response) {
      yield put(
        accountActivityErrorMethod(
          `Error ${error.response.status}: ${error.response.data}`
        )
      );
    } else if (error.request) {
      yield put(
        accountActivityErrorMethod("No response received from server.")
      );
    } else {
      yield put(accountActivityErrorMethod(`Request error: ${error.message}`));
    }
  }
}

// 👂 Watchers
function* watchOrganizationalUnits() {
  yield takeLatest(
    "organizationalUnits/getOrganizationalUnits",
    fetchOrganizationalUnits
  );
  yield takeLatest("organizationalUnits/generateYaml", generateYamlSaga);
  yield takeLatest("organizationalUnits/generateEventInsightYaml", generateEventInsightYamlSaga);
  yield takeLatest(
    "organizationalUnits/checkStackStatusMethod",
    fetchStackStatusSaga
  );
  yield takeLatest(
    "organizationalUnits/checkAccountActivityMethod",
    fetchAccountStatusSaga
  );
}

// 🧠 Root Saga
export function* organizationalUnitsSaga() {
  yield all([fork(watchOrganizationalUnits)]);
}

export default organizationalUnitsSaga;
